import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { FormattedText } from "@faire/retailer-visitor-shared/components/FormattedText";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Span, Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackPromotionalEventsPdpPromoBannerClick } from "@faire/web-api--source/events/promotionalEvents/click/pdpPromoBanner";
import { trackPromotionalEventsPdpPromoBannerView } from "@faire/web-api--source/events/promotionalEvents/view/pdpPromoBanner";
import { IInformationMessage } from "@faire/web-api--source/indigofair/data/IInformationMessage";
import { observer } from "mobx-react";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import { CtaLink } from "./CtaLink";
import { PromoIcon } from "./PromoIcon";
var ProductPromoBannersBase = function(props) {
    var _props_promotions;
    var _useState = _sliced_to_array(useState(-1), 2), tooltipIndex = _useState[0], setTooltipIndex = _useState[1];
    if (!((_props_promotions = props.promotions) === null || _props_promotions === void 0 ? void 0 : _props_promotions.length)) {
        return null;
    }
    var promotionsToShow = props.promotions.filter(function(productPromotion) {
        var _productPromotion_position;
        var bannerPosition = (_productPromotion_position = productPromotion.position) !== null && _productPromotion_position !== void 0 ? _productPromotion_position : IInformationMessage.Position.DEFAULT;
        return productPromotion.type !== IInformationMessage.Type.CATEGORY_RESTRICTION && productPromotion.type !== IInformationMessage.Type.GUARANTEED_FREE_SHIPPING_THRESHOLD && bannerPosition === props.position;
    });
    if (!(promotionsToShow === null || promotionsToShow === void 0 ? void 0 : promotionsToShow.length)) {
        return null;
    }
    var toggleTooltip = function(index) {
        return setTooltipIndex(function(currentIndex) {
            return currentIndex === index ? -1 : index;
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                size: "2x"
            }),
            /*#__PURE__*/ _jsx(Column, {
                gap: "2x",
                children: promotionsToShow.map(function(productPromotion, index) {
                    return /*#__PURE__*/ _jsx(ProductPromoBanner, {
                        productPromotion: productPromotion,
                        index: index,
                        toggleTooltip: toggleTooltip,
                        tooltipIndex: tooltipIndex
                    }, index);
                })
            })
        ]
    });
};
export var ProductPromoBanners = observer(ProductPromoBannersBase);
var ProductPromoBanner = function(param) {
    var productPromotion = param.productPromotion, index = param.index, toggleTooltip = param.toggleTooltip, tooltipIndex = param.tooltipIndex;
    var viewRef = useDoActionWhenVisible(function() {
        var _productPromotion_type;
        return trackPromotionalEventsPdpPromoBannerView((_productPromotion_type = productPromotion.type) !== null && _productPromotion_type !== void 0 ? _productPromotion_type : IInformationMessage.Type.TYPE_UNKNOWN);
    });
    return /*#__PURE__*/ _jsxs(ProductPromotionContainer, {
        ref: viewRef,
        onClick: function() {
            return toggleTooltip(index);
        },
        $bgColor: productPromotion.background_color,
        "data-test-id": "productPromoBanner:".concat(productPromotion.type),
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                size: 10
            }),
            /*#__PURE__*/ _jsx(PromoIcon, _object_spread({}, productPromotion)),
            /*#__PURE__*/ _jsxs(PromotionTypography, {
                $textColor: productPromotion.text_color,
                children: [
                    productPromotion.title ? /*#__PURE__*/ _jsx(Title, {
                        color: productPromotion.title_color,
                        children: productPromotion.title
                    }) : null,
                    productPromotion.message_prefix ? /*#__PURE__*/ _jsx(MessagePrefix, {
                        color: productPromotion.prefix_color,
                        children: productPromotion.message_prefix
                    }) : null,
                    /*#__PURE__*/ _jsx(FormattedText, {
                        text: productPromotion.text
                    }),
                    productPromotion.tooltip ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "\xa0",
                            /*#__PURE__*/ _jsxs(Tooltip, {
                                isOpen: tooltipIndex === index,
                                innerClassName: "tooltip-inner",
                                pointerOffset: 130,
                                children: [
                                    productPromotion.tooltip,
                                    productPromotion.help_link ? /*#__PURE__*/ _jsx(EmbeddedLink, {
                                        target: "_blank",
                                        linkTo: productPromotion.help_link,
                                        color: Core.text.inverse,
                                        onClick: function() {
                                            var _productPromotion_type;
                                            trackPromotionalEventsPdpPromoBannerClick((_productPromotion_type = productPromotion.type) !== null && _productPromotion_type !== void 0 ? _productPromotion_type : IInformationMessage.Type.TYPE_UNKNOWN);
                                            toggleTooltip(index);
                                        },
                                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                            id: "7DIW6+",
                                            defaultMessage: [
                                                {
                                                    type: 0,
                                                    value: "Learn More"
                                                }
                                            ]
                                        })
                                    }) : null
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "\xa0",
                            /*#__PURE__*/ _jsx(CtaLink, _object_spread({}, productPromotion))
                        ]
                    })
                ]
            })
        ]
    });
};
var HighlightedText = styled(Span).attrs({
    variant: "paragraphSansMedium"
}).withConfig({
    componentId: "sc-6df3a2a2-0"
})([
    "",
    ";",
    ";"
], function(param) {
    var $customColor = param.$customColor;
    return $customColor ? css([
        "color:",
        ";"
    ], css([
        "#",
        " !important"
    ], $customColor)) : "";
}, function(param) {
    var $withSpacing = param.$withSpacing;
    return $withSpacing ? "\n        margin-bottom: 4px;\n        display: block;\n      " : "";
});
var Title = function(param) {
    var children = param.children, color = param.color;
    return /*#__PURE__*/ _jsx(HighlightedText, {
        $withSpacing: true,
        $customColor: color,
        children: children
    });
};
var MessagePrefix = function(param) {
    var children = param.children, color = param.color;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(HighlightedText, {
                $customColor: color,
                children: children
            }),
            " "
        ]
    });
};
var ProductPromotionContainer = styled(Row).attrs({
    align: "flex-start"
}).withConfig({
    componentId: "sc-6df3a2a2-1"
})([
    "width:100%;border-radius:4px;",
    ";"
], function(props) {
    return props.$bgColor ? css([
        "background:#",
        ";padding:",
        ";"
    ], props.$bgColor, getSpacing("2x")) : "";
});
var PromotionTypography = styled(Typography).withConfig({
    componentId: "sc-6df3a2a2-2"
})([
    "&&&{color:",
    ";}"
], function(param) {
    var $textColor = param.$textColor;
    return $textColor ? css([
        "#",
        ""
    ], $textColor) : Core.text.default;
});
